import { Component, EventEmitter, OnInit, Output } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { TrustService } from "@fiduciaria/Shared/Services/trust.service";
import { RequestStatus } from "@fiduciaria/Shared/Types/bizagi-option.interface";
import { Dependency } from "@fiduciaria/Shared/Types/dependency.interface";
import { Escrow } from "@fiduciaria/Shared/Types/escrow.interface";
import { TrustRequestsFilter } from "@fiduciaria/Shared/Types/requests-filter.interface";
import { TrustSubType, TrustType } from "@fiduciaria/Shared/Types/type.interface";
import { Observable } from "rxjs";

import { SearchByFormService } from "@shared/FormShared/Services/search-by-form.service";
import { CustomValidators } from "@shared/FormShared/Validators/custom-validators.validator";

import { FiduciarySearchTerms } from "../../Types/Enums/fiduciary-search-term.enum";
import { UsuariosRepository } from "@shared/Repositories/usuarios.repository";

@Component({
  selector: "app-fiduciary-search-form",
  templateUrl: "./fiduciary-search-form.component.html",
})
export class FiduciarySearchFormComponent implements OnInit {
  public form: FormGroup;
  public status$: Observable<RequestStatus[]>;
  public types$: Observable<TrustType[]>;
  public subTypes$: Observable<TrustSubType[]>;
  public dependencies$: Observable<Dependency[]>;
  public escrows$: Observable<Escrow[]>;
  public isUserFidiciary: boolean;

  @Output() public submitForm = new EventEmitter<TrustRequestsFilter>();

  public readonly FiduciarySearchTerms = FiduciarySearchTerms;
  public readonly searchTerms = [
    FiduciarySearchTerms.Date,
    FiduciarySearchTerms.Escrow,
    FiduciarySearchTerms.Application,
    FiduciarySearchTerms.Status,
    FiduciarySearchTerms.ApplicationTypes,
    FiduciarySearchTerms.All,
  ];

  readonly rolesPermitidos = [
    "Canales Fiduciaria",
    "Administrador Corporativo Fiduciaria",
    "Operativo Fiduciaria"
  ];

  constructor(
    private searchByFormService: SearchByFormService,
    private formBuilder: FormBuilder,
    public trustService: TrustService,
    private readonly userRepository: UsuariosRepository
  ) {}

  public ngOnInit(): void {
    
    this.buildForm();
    this.getDropdownOptions();
  }

  public getDropdownOptions(): void {
    this.status$ = this.trustService.getRequestStatus();
    this.dependencies$ = this.trustService.getDependencies();
    this.userRepository.getInfoClienteActual().subscribe((usuario) => {
      this.isUserFidiciary = usuario.usuarioRole.some((x) => this.rolesPermitidos.includes(x.role.nombre));
      const isUserCanalesFidiciary = usuario.usuarioRole.some((x) => this.rolesPermitidos[0] === x.role.nombre);

      if (isUserCanalesFidiciary) {
        this.escrows$ =  this.trustService.getEscrowsAll();
      } else if (this.isUserFidiciary) {
        this.escrows$ =  this.trustService.getUserFideicomisos();
      } else {
        this.escrows$ = this.trustService.getEscrows();
      }
    });    
  }

  public buildForm(): void {
    this.form = this.formBuilder.group(
      {
        searchTerm: [null, Validators.required],
        dateRange: [null, Validators.required],
        fideicomiso: [
          null,
          [CustomValidators.ConditionalValidatorBySearchTerm(FiduciarySearchTerms.Escrow, CustomValidators.Required)],
        ],
        requestId: [
          null,
          [CustomValidators.ConditionalValidatorBySearchTerm(FiduciarySearchTerms.Application, CustomValidators.Required)],
        ],
        status: [
          null,
          [CustomValidators.ConditionalValidatorBySearchTerm(FiduciarySearchTerms.Status, CustomValidators.Required)],
        ],
        dependency: [
          null,
          [CustomValidators.ConditionalValidatorBySearchTerm(FiduciarySearchTerms.ApplicationTypes, CustomValidators.Required)],
        ],
        type: [
          null,
          [CustomValidators.ConditionalValidatorBySearchTerm(FiduciarySearchTerms.ApplicationTypes, CustomValidators.Required)],
        ],
        subType: [],
      },
      {
        validators: [CustomValidators.AtLeastOneValid()],
      }
    );
    this.searchByFormService.startForm(this.form);

    this.form.get("searchTerm").valueChanges.subscribe((searchTerm: FiduciarySearchTerms) => {
      const dateInput = this.form.get("dateRange");
      if (searchTerm !== FiduciarySearchTerms.Application) {
        dateInput.setValidators([Validators.required]);
      } else {
        dateInput.clearValidators();
      }
      dateInput.updateValueAndValidity();
    });

    this.form.get("dependency").valueChanges.subscribe((dependencyKey: string) => {
      this.form.get("type").reset();
      this.form.get("subType").reset();
      this.types$ = dependencyKey ? this.trustService.getTypes(dependencyKey) : null;
      this.subTypes$ = null;
    });

    this.form.get("type").valueChanges.subscribe((key: string) => {
      this.form.get("subType").reset();
      this.subTypes$ = key ? this.trustService.getSubTypes(key) : null;
    });
  }

  public onSubmitForm(): void {
    const value = this.searchByFormService.cleanFormValue(this.form);
    this.submitForm.emit(value);
  }

  public get searchTerm(): FiduciarySearchTerms {
    return this.form.get("searchTerm").value ?? "";
  }
}
